import { jsxs, jsx } from 'react/jsx-runtime';
import { useResourceBookmarkDataSafe } from '../../../../../resource-detail/components/context/resourceDataContext.js';
import { ColorLabel } from '../../components/ColorLabel.js';
import { ResourcePreviewFavicon } from '../../components/ResourcePreviewFavicon.js';
import { ResourcePreviewTitle, ResourcePreviewDescription } from '../../components/Typography.js';
import { containerPreviewCompact } from '../cssPreviewContainer.js';
import { cssAspectRatio } from '../sharedCss.js';
import { styled, css } from 'styled-components';
import { IconBookmark } from '@fabric-app/icons/svg';
import { TWITTER_URL_REGEX, SPOTIFY_URL_REGEX } from '../../../../../expandedResource/expandedResource.utils.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssLineClamp } from '@fabric-app/ui/constants/styled-utils';
import { Image } from '@fabric-app/ui/components/Image';
import { cleanUrl } from '@fabric-app/utils/url';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { ResourceGridViewLoadingIndicator } from '../components/ResourceGridViewLoadingIndicator.js';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { isResourceStateProcessing } from '../../../../utils/isResourceStateProcessing.js';
import Skeleton from '@fabric-app/ui/components/Skeleton';
import { useResourceListPreferencesContextUnsafe } from '../../../ResourceList/contexts/resourceListContextPreferences.js';

const ResourcePreviewPage = () => {
  const { resource, placeholderName } = useResourceBookmarkDataSafe();
  const isProcessing = isResourceStateProcessing(resource.stateProcessing);
  const coverSrc = resource.thumbnail?.md || resource.data.webpage?.image?.url;
  const pageDescription = resource.data.webpage?.description;
  const url = resource.originUrl;
  const pref = useResourceListPreferencesContextUnsafe();
  const isAutoHeight = pref?.viewMode === "Masonry";
  useRunOnce(() => {
    captureException(new Error("Tried to render bookmark preview. Missing originUrl"), {
      extra: resource
    });
  }, {
    condition: !url
  });
  const isTwitter = url?.match(TWITTER_URL_REGEX);
  const twitterHandle = url && isTwitter ? new URL(url).pathname.split("/")[1] : void 0;
  const isSpotify = url?.match(SPOTIFY_URL_REGEX);
  const spotifyArtist = resource.data.webpage?.metadata && isSpotify && "artist" in resource.data.webpage?.metadata && resource.data.webpage?.metadata.artist;
  return /* @__PURE__ */ jsxs(Wrapper, {
    "data-preview-content-wrapper": "page",
    isAutoHeight,
    children: [
      url && /* @__PURE__ */ jsx(ResourcePreviewFavicon, {
        pageUrl: url,
        position: "absolute-top-left",
        faviconUrl: resource.data.webpage?.favicon?.url
      }),
      /* @__PURE__ */ jsxs(ThumbnailWrapper, {
        children: [
          /* @__PURE__ */ jsx(ColorLabel, {
            absPosition: "inside-top-left"
          }),
          /* @__PURE__ */ jsx(ResourceGridViewLoadingIndicator, {
            resource
          }),
          coverSrc ? /* @__PURE__ */ jsx(ThumbnailImage, {
            src: coverSrc,
            alt: resource.name || "Unknown",
            draggable: false
          }) : /* @__PURE__ */ jsx(EmptyImageState, {
            children: /* @__PURE__ */ jsx(IconBookmark, {
              style: {
                width: "4.25rem",
                height: "4.25rem"
              }
            })
          })
        ]
      }),
      /* @__PURE__ */ jsxs(PageInfo, {
        children: [
          (isProcessing || resource.isDraft) && (resource.name === null || resource.name === "") ? /* @__PURE__ */ jsx(Skeleton, {
            height: 16,
            width: "80%",
            borderRadius: 6
          }) : /* @__PURE__ */ jsxs(ResourcePreviewTitle, {
            children: [
              twitterHandle && /* @__PURE__ */ jsxs(TwitterHandle, {
                children: [
                  "@",
                  twitterHandle,
                  " "
                ]
              }),
              resource.name || placeholderName
            ]
          }),
          !isTwitter && url && /* @__PURE__ */ jsx(ResourcePreviewDescription, {
            children: spotifyArtist || cleanUrl(url)
          }),
          isTwitter && pageDescription && /* @__PURE__ */ jsx(PageDescription, {
            children: pageDescription
          })
        ]
      })
    ]
  });
};
const PageInfo = styled.div.withConfig({
  displayName: "ResourcePreviewPage__PageInfo",
  componentId: "sc-875a27a8-0"
})([
  `display:flex;flex-direction:column;gap:0.3125rem;`
]);
const Wrapper = styled.div.withConfig({
  displayName: "ResourcePreviewPage__Wrapper",
  componentId: "sc-875a27a8-1"
})([
  `width:100%;height:100%;display:flex;flex-direction:column;flex-grow:1;gap:0.625rem;overflow:hidden;justify-content:center;padding:0.75rem;border:var(--preview-border-width,2px) solid var(--preview-border-color,`,
  `);background:var(--preview-background-color,`,
  `);border-radius:var(--preview-content-border-radius,1.125rem);position:relative;`,
  ` `,
  `{border:none;border-radius:0;padding:0.6875rem;}`
], cssVar["color-border-secondary"], cssVar["color-bg-primary"], ({ isAutoHeight }) => !isAutoHeight ? cssAspectRatio : css([
  `min-height:150px;max-height:700px;`
]), containerPreviewCompact);
const ThumbnailWrapper = styled(Flex).attrs({
  direction: "column",
  justifyContent: "center",
  alignItems: "center"
}).withConfig({
  displayName: "ResourcePreviewPage__ThumbnailWrapper",
  componentId: "sc-875a27a8-2"
})([
  `flex-grow:1;flex-shrink:1;overflow:hidden;`
]);
const ThumbnailImage = styled(Image).withConfig({
  displayName: "ResourcePreviewPage__ThumbnailImage",
  componentId: "sc-875a27a8-3"
})([
  `max-width:100%;min-height:1px;object-fit:scale-down;border:1px solid `,
  `;display:block;border-radius:0.5rem;background:`,
  `;`
], cssVar["color-border-secondary"], cssVar["color-bg-section"]);
const EmptyImageState = styled.div.withConfig({
  displayName: "ResourcePreviewPage__EmptyImageState",
  componentId: "sc-875a27a8-4"
})([
  `height:100;flex-grow:1;width:100%;max-width:100%;max-height:100%;display:flex;align-items:center;justify-content:center;border-radius:0.5rem;background:`,
  `;color:`,
  `;`
], cssVar["color-bg-section"], cssVar["color-bg-primary"]);
const PageDescription = styled.p.withConfig({
  displayName: "ResourcePreviewPage__PageDescription",
  componentId: "sc-875a27a8-5"
})([
  `color:`,
  `;font-size:0.8125rem;line-height:1.25rem;`,
  ` `,
  `{font-size:0.75rem;line-height:1rem;`,
  `}`
], cssVar["color-text-quaternary"], cssLineClamp(3), containerPreviewCompact, cssLineClamp(2));
const TwitterHandle = styled.span.withConfig({
  displayName: "ResourcePreviewPage__TwitterHandle",
  componentId: "sc-875a27a8-6"
})([
  `color:`,
  `;opacity:0.8;`
], cssVar["color-text-quaternary"]);

export { ResourcePreviewPage };
//# sourceMappingURL=ResourcePreviewPage.js.map
